import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../services/firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const ClientProfile = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    profilePicture: '',
    bio: '',
    preferences: {
      emailNotifications: true,
      smsNotifications: false,
      marketingEmails: false
    }
  });
  const [originalData, setOriginalData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (currentUser) {
      loadProfileData();
    }
  }, [currentUser]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    // Check if there are any changes
    const hasProfileChanges = JSON.stringify(profileData) !== JSON.stringify(originalData);
    setHasChanges(hasProfileChanges);
  }, [profileData, originalData]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadProfileData = async () => {
    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const profileInfo = {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || currentUser.email || '',
          phone: userData.phone || '',
          address: userData.address || '',
          city: userData.city || '',
          state: userData.state || '',
          zipCode: userData.zipCode || '',
          profilePicture: userData.profilePicture || '',
          bio: userData.bio || '',
          preferences: {
            emailNotifications: userData.preferences?.emailNotifications ?? true,
            smsNotifications: userData.preferences?.smsNotifications ?? false,
            marketingEmails: userData.preferences?.marketingEmails ?? false
          }
        };
        setProfileData(profileInfo);
        setOriginalData(profileInfo);
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      showNotification('Failed to load profile data. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      // Handle nested fields like preferences.emailNotifications
      const [parent, child] = field.split('.');
      setProfileData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('Starting profile picture upload...', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId: currentUser.uid
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('Please select a valid image file.', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('Image size must be less than 5MB.', 'error');
      return;
    }

    setUploading(true);
    try {
      // Delete old profile picture if it exists
      if (profileData.profilePicture) {
        try {
          console.log('Attempting to delete old profile picture...');
          const oldImageRef = ref(storage, `temp/${currentUser.uid}/profile.jpg`);
          await deleteObject(oldImageRef);
          console.log('Old profile picture deleted successfully');
        } catch (deleteError) {
          console.warn('Could not delete old profile picture (this is normal if no old picture exists):', deleteError);
        }
      }

      // Upload new profile picture using temp path (temporary solution)
      console.log('Uploading new profile picture...');
      const imageRef = ref(storage, `temp/${currentUser.uid}/profile.jpg`);
      console.log('Storage reference created:', imageRef.fullPath);
      
      await uploadBytes(imageRef, file);
      console.log('File uploaded successfully');
      
      const downloadURL = await getDownloadURL(imageRef);
      console.log('Download URL obtained:', downloadURL);

      // Update profile data
      setProfileData(prev => ({
        ...prev,
        profilePicture: downloadURL
      }));

      showNotification('Profile picture updated successfully!', 'success');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        storagePath: `users/${currentUser.uid}/profile/profile.jpg`
      });
      
      let errorMessage = 'Failed to upload profile picture. Please try again.';
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'Permission denied. Please check your account permissions.';
      } else if (error.code === 'storage/object-not-found') {
        errorMessage = 'Storage path not found. Please try again.';
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!hasChanges) {
      showNotification('No changes to save.', 'info');
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: new Date()
      });

      setOriginalData({ ...profileData });
      showNotification('Profile updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification('Failed to update profile. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetChanges = () => {
    setProfileData({ ...originalData });
    showNotification('Changes reset successfully.', 'info');
  };

  const removeProfilePicture = async () => {
    if (!profileData.profilePicture) return;

    setUploading(true);
    try {
      // Delete from Firebase Storage
      const imageRef = ref(storage, `temp/${currentUser.uid}/profile.jpg`);
      await deleteObject(imageRef);

      // Update profile data
      setProfileData(prev => ({
        ...prev,
        profilePicture: ''
      }));

      showNotification('Profile picture removed successfully!', 'success');
    } catch (error) {
      console.error('Error removing profile picture:', error);
      showNotification('Failed to remove profile picture. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Layout>
      <div className="profile-page" style={{ 
        padding: windowWidth <= 640 ? '0.5rem' : windowWidth <= 768 ? '1rem' : '1rem 2rem',
        maxWidth: '100%',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Toast Notification */}
        {notification && (
          <div className="notification" style={{
            position: 'fixed',
            top: '20px',
            right: windowWidth <= 640 ? '0.5rem' : '20px',
            left: windowWidth <= 640 ? '0.5rem' : 'auto',
            zIndex: 1000,
            maxWidth: windowWidth <= 640 ? 'calc(100% - 1rem)' : '400px',
            padding: windowWidth <= 640 ? '0.75rem 1rem' : '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backgroundColor: notification.type === 'success' ? '#4caf50' : 
                           notification.type === 'error' ? '#f44336' : '#2196f3',
            color: 'white',
            fontSize: windowWidth <= 640 ? '0.8rem' : '0.875rem',
            fontWeight: '500'
          }}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: windowWidth <= 640 ? '1.5rem' : '2rem', fontWeight: 'bold', color: '#2e7d32', margin: 0 }}>
            👤 My Profile
        </h1>
          <p style={{ color: '#666', marginTop: '0.5rem', fontSize: windowWidth <= 640 ? '0.875rem' : '1rem' }}>
            Manage your personal information and preferences
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
            <p>Loading profile...</p>
          </div>
        ) : (
          <div className="profile-grid" style={{ 
            display: windowWidth <= 640 ? 'flex' : 'grid',
            flexDirection: windowWidth <= 640 ? 'column' : 'row',
            gridTemplateColumns: windowWidth <= 768 ? '1fr' : '1fr 2fr', 
            gap: windowWidth <= 640 ? '1rem' : '2rem',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}>
            {/* Profile Picture Section */}
            <div className="profile-card" style={{
              background: 'white',
              borderRadius: '12px',
              padding: windowWidth <= 640 ? '1rem' : '2rem',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              height: 'fit-content',
              marginBottom: windowWidth <= 768 ? '1rem' : '0',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'hidden'
            }}>
              <h2 style={{ margin: '0 0 1.5rem 0', fontSize: windowWidth <= 640 ? '1.125rem' : '1.25rem', fontWeight: '600', color: '#1f2937' }}>
                Profile Picture
              </h2>
              
              <div style={{ textAlign: 'center' }}>
                <div className="profile-picture" style={{
                  width: windowWidth <= 640 ? '120px' : '150px',
                  height: windowWidth <= 640 ? '120px' : '150px',
                  borderRadius: '50%',
                  margin: '0 auto 1.5rem auto',
                  backgroundColor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: '3px solid #e5e7eb'
                }}>
                  {profileData.profilePicture ? (
                    <img
                      src={profileData.profilePicture}
                      alt="Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '4rem', color: '#9ca3af' }}>👤</div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  style={{ display: 'none' }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    style={{
                      background: uploading ? '#9ca3af' : '#2e7d32',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1rem',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    {uploading ? '📤 Uploading...' : '📤 Upload Photo'}
                  </button>

                  {profileData.profilePicture && (
                    <button
                      onClick={removeProfilePicture}
                      disabled={uploading}
                      style={{
                        background: uploading ? '#9ca3af' : '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.5rem 1rem',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600'
                      }}
                    >
                      🗑️ Remove Photo
                    </button>
                  )}
                </div>

                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '1rem' }}>
                  JPG, PNG or GIF. Max size 5MB.
                </p>
              </div>
            </div>

            {/* Profile Information Section */}
            <div className="profile-card" style={{
              background: 'white',
              borderRadius: '12px',
              padding: windowWidth <= 640 ? '1rem' : '2rem',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'hidden'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: windowWidth <= 640 ? 'flex-start' : 'center',
                flexDirection: windowWidth <= 640 ? 'column' : 'row',
                gap: windowWidth <= 640 ? '1rem' : '0',
                marginBottom: '2rem' 
              }}>
                <h2 style={{ margin: 0, fontSize: windowWidth <= 640 ? '1.125rem' : '1.25rem', fontWeight: '600', color: '#1f2937' }}>
                  Personal Information
                </h2>
                
                {hasChanges && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.75rem',
                    flexDirection: windowWidth <= 640 ? 'column' : 'row',
                    width: windowWidth <= 640 ? '100%' : 'auto'
                  }}>
                    <button
                      onClick={handleResetChanges}
                      style={{
                        background: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      Reset Changes
                    </button>
                    <button
                      onClick={handleSaveProfile}
                      disabled={loading}
                      style={{
                        background: loading ? '#9ca3af' : '#2e7d32',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.5rem 1.5rem',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600'
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: windowWidth <= 640 ? '1fr' : '1fr 1fr', 
                gap: windowWidth <= 640 ? '1rem' : '1.5rem', 
                marginBottom: '2rem',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Enter your first name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Enter your last name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: windowWidth <= 640 ? '1fr' : '1fr 1fr', 
                gap: windowWidth <= 640 ? '1rem' : '1.5rem', 
                marginBottom: '2rem',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter your email"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      backgroundColor: '#f9fafb'
                    }}
                    readOnly
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Email cannot be changed here. Contact support if needed.
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={profileData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter your street address"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: windowWidth <= 640 ? '1fr' : windowWidth <= 768 ? '1fr 1fr' : '1fr 1fr 1fr', 
                gap: windowWidth <= 640 ? '1rem' : '1.5rem', 
                marginBottom: '2rem',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="Enter city"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    State
                  </label>
                  <input
                    type="text"
                    value={profileData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="Enter state"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={profileData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder="12345"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Bio (Optional)
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows="3"
                  placeholder="Tell us a bit about yourself..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Preferences Section */}
              <div style={{
                borderTop: '1px solid #e5e7eb',
                paddingTop: '2rem'
              }}>
                <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                  Notification Preferences
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={profileData.preferences.emailNotifications}
                      onChange={(e) => handleInputChange('preferences.emailNotifications', e.target.checked)}
                      style={{ width: '1rem', height: '1rem' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                        Email Notifications
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        Receive updates about appointments and estimates via email
                      </div>
                    </div>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={profileData.preferences.smsNotifications}
                      onChange={(e) => handleInputChange('preferences.smsNotifications', e.target.checked)}
                      style={{ width: '1rem', height: '1rem' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                        SMS Notifications
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        Receive text message reminders and updates
                      </div>
                    </div>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={profileData.preferences.marketingEmails}
                      onChange={(e) => handleInputChange('preferences.marketingEmails', e.target.checked)}
                      style={{ width: '1rem', height: '1rem' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                        Marketing Emails
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        Receive promotional offers and seasonal tips
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Save Button at Bottom */}
              {hasChanges && (
                <div style={{
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: '2rem',
                  marginTop: '2rem',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '1rem'
                }}>
                  <button
                    onClick={handleResetChanges}
                    style={{
                      background: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    Reset Changes
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={loading}
                    style={{
                      background: loading ? '#9ca3af' : '#2e7d32',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 2rem',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}
                  >
                    {loading ? '💾 Saving...' : '💾 Save Profile'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientProfile;